import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Radio, LineChart, FileText, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/Marketing/MarketingNav";
import { MarketingFooter } from "@/components/Marketing/MarketingFooter";
import { MarketingFinalCta } from "@/components/Marketing/MarketingFinalCta";

const PILLARS = [
  {
    icon: BarChart3,
    title: "AI article scoring",
    body: "Every submission is rated on Accuracy, Tone, Reach and Quality, with human override always available and a full audit trail behind every score so nothing is a black box.",
  },
  {
    icon: Radio,
    title: "Mention listening",
    body: "Real-time brand and topic monitoring across news sites and social, in English, French and Portuguese, so you hear about coverage before someone forwards you a screenshot.",
  },
  {
    icon: LineChart,
    title: "Share of voice",
    body: "Benchmark your institution's coverage against peers, week over week, channel by channel, with trends you can actually act on instead of a vanity number.",
  },
  {
    icon: FileText,
    title: "Board-ready PR reports",
    body: "One click to export a polished PDF or CSV your principal can read on a Sunday night, no manual collation, no copy-pasting screenshots into a slide deck.",
  },
];

const SCORE_COMPONENTS = [
  { k: "Accuracy", v: "96" },
  { k: "Tone", v: "91" },
  { k: "Reach", v: "88" },
  { k: "Quality", v: "93" },
];

export default function CoveragePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />

      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-24 overflow-hidden">
        <div className="container mx-auto px-4 relative">
          <div className="max-w-2xl mb-14">
            <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">Coverage Intelligence</span>
            <h1 className="font-display text-4xl md:text-5xl mt-3 text-foreground text-balance">
              The <span className="text-gradient-gold">PR analytics tool</span> the market has been missing.
            </h1>
            <p className="text-justify text-muted-foreground mt-4 text-lg leading-relaxed">
              Visibility you can defend in a board meeting. Sentiment you can act on. Reach
              you can prove. CONORBIT measures every piece of coverage with the rigour your
              institution deserves, from the first mention to the final report.
            </p>
            <div className="mt-7">
              <Link to="/auth?mode=signup">
                <Button size="lg" className="gap-2 h-12 px-7">
                  Request institution access <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 items-start">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-md">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Coverage score · last 30 days</div>
                  <div className="font-display text-4xl text-gradient-emerald mt-1">94.7</div>
                </div>
                <span className="text-[11px] text-primary-glow inline-flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" /> +6.2 vs. prior
                </span>
              </div>
              <svg viewBox="0 0 300 80" className="w-full h-20">
                <polyline
                  fill="none"
                  stroke="hsl(var(--accent))"
                  strokeWidth="2"
                  points="0,55 25,48 50,52 75,40 100,44 125,30 150,34 175,22 200,28 225,18 250,24 275,12 300,16"
                />
                <polyline
                  fill="hsl(var(--accent) / 0.15)"
                  stroke="none"
                  points="0,55 25,48 50,52 75,40 100,44 125,30 150,34 175,22 200,28 225,18 250,24 275,12 300,16 300,80 0,80"
                />
              </svg>
              <div className="grid grid-cols-4 gap-3 mt-6">
                {SCORE_COMPONENTS.map((m) => (
                  <div key={m.k} className="rounded-xl border border-border bg-background/60 p-3 text-center">
                    <div className="font-display text-xl text-foreground">{m.v}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{m.k}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {PILLARS.map((p) => (
                <div key={p.title} className="rounded-2xl border border-border bg-card p-6 hover-scale">
                  <p.icon className="w-5 h-5 text-accent mb-3" />
                  <h3 className="font-display text-base text-foreground">{p.title}</h3>
                  <p className="text-justify text-sm text-muted-foreground mt-1.5 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 border-t border-border bg-muted/20">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl text-foreground">How scoring works</h2>
          <p className="text-justify text-muted-foreground mt-4 leading-relaxed">
            When a journalist submits coverage, an AI pass scores it across four weighted
            components, accuracy of the reporting, tone toward your institution, estimated reach
            of the outlet, and overall production quality. Your team can review and override any
            score with a note attached, and the override, the original AI score, and the reason
            are all preserved in the audit trail. Over time this gives your communications office
            a defensible, consistent record of who is covering you well, and who isn't, without
            relying on gut feel or who shouts loudest in the weekly meeting.
          </p>
        </div>
      </section>

      <MarketingFinalCta />
      <MarketingFooter />
    </div>
  );
}
